import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CreateDonorRequest } from '@/types';
import { offlineStorageService } from '@/services/offlineStorage';

type DonorFormData = {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  gdprConsent: boolean;
  marketingConsent?: boolean;
  fiscalCode?: string;
  vatNumber?: string;
  sdiCode?: string;
  pec?: string;
};

const donorSchema = yup.object({
  email: yup
    .string()
    .email('Email non valida')
    .required('Email obbligatoria'),
  firstName: yup
    .string()
    .min(2, 'Nome deve essere di almeno 2 caratteri')
    .required('Nome obbligatorio'),
  lastName: yup
    .string()
    .min(2, 'Cognome deve essere di almeno 2 caratteri')
    .required('Cognome obbligatorio'),
  company: yup.string().optional(),
  phone: yup.string().optional(),
  address: yup.string().optional(),
  city: yup.string().optional(),
  postalCode: yup.string().optional(),
  country: yup.string().optional(),
  gdprConsent: yup
    .boolean()
    .required('Devi accettare il consenso GDPR'),
  marketingConsent: yup.boolean().optional(),
  fiscalCode: yup.string().optional().test('fiscal-code-format', 'Il codice fiscale deve essere di 16 caratteri alfanumerici', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length !== 16) return false;
    return /^[A-Z0-9]{16}$/.test(value);
  }),
  vatNumber: yup.string().optional().test('vat-number-format', 'La partita IVA deve essere di 11 cifre', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length !== 11) return false;
    return /^[0-9]{11}$/.test(value);
  }),
  sdiCode: yup.string().optional().test('sdi-code-format', 'Il codice SDI deve essere di 6-7 caratteri alfanumerici maiuscoli', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length < 6 || value.length > 7) return false;
    return /^[A-Z0-9]{6,7}$/.test(value);
  }),
  pec: yup.string().email('PEC non valida').optional(),
});

interface DonorFormProps {
  data: CreateDonorRequest;
  onChange: (data: Partial<CreateDonorRequest>) => void;
  onNext: () => void;
  disabled: boolean;
}

const ITALIAN_CITIES = [
  'Milano', 'Roma', 'Napoli', 'Torino', 'Palermo', 'Genova', 'Bologna', 'Firenze',
  'Bari', 'Catania', 'Venezia', 'Verona', 'Messina', 'Padova', 'Trieste', 'Brescia',
  'Parma', 'Taranto', 'Prato', 'Reggio Calabria', 'Modena', 'Piacenza', 'Livorno',
  'Ravenna', 'Cagliari', 'Foggia', 'Rimini', 'Salerno', 'Ferrara', 'Sassari',
  'Monza', 'Latina', 'Bergamo', 'Forlì', 'Trento', 'Vicenza', 'Terni', 'Bolzano',
  'Novara', 'Piacenza', 'Ancona', 'Andria', 'Arezzo', 'Udine', 'Cesena', 'Lecce'
];

export const DonorForm: React.FC<DonorFormProps> = ({ data, onChange, onNext, disabled }) => {
  const [searchResults, setSearchResults] = useState<CreateDonorRequest[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<DonorFormData>({
    resolver: yupResolver(donorSchema),
    defaultValues: {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      company: data.company || '',
      phone: data.phone || '',
      address: data.address || '',
      city: data.city || '',
      postalCode: data.postalCode || '',
      country: data.country || 'IT',
      gdprConsent: data.gdprConsent || false,
      marketingConsent: data.marketingConsent || false,
      fiscalCode: data.fiscalCode || '',
      vatNumber: data.vatNumber || '',
      sdiCode: data.sdiCode || '',
      pec: data.pec || '',
    },
    mode: 'onChange',
  });

  const email = watch('email');

  // Cerca donatori esistenti quando cambia l'email
  useEffect(() => {
    const searchDonors = async () => {
      if (email && email.length > 3) {
        setIsSearching(true);
        try {
          const results = await offlineStorageService.searchOfflineDonors(email);
          setSearchResults(results);
          setShowSearchResults(results.length > 0);
        } catch (error) {
          console.warn('Search failed:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    };

    const timeoutId = setTimeout(searchDonors, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleFormChange = (field: keyof CreateDonorRequest, value: any) => {
    setValue(field, value);
    onChange({ [field]: value });
  };

  const handleSelectDonor = (donor: CreateDonorRequest) => {
    setValue('email', donor.email);
    setValue('firstName', donor.firstName);
    setValue('lastName', donor.lastName);
    setValue('company', donor.company || '');
    setValue('phone', donor.phone || '');
    setValue('address', donor.address || '');
    setValue('city', donor.city || '');
    setValue('postalCode', donor.postalCode || '');
    setValue('country', donor.country || 'IT');
    setValue('gdprConsent', donor.gdprConsent);
    setValue('marketingConsent', donor.marketingConsent || false);
    setValue('fiscalCode', donor.fiscalCode || '');
    setValue('vatNumber', donor.vatNumber || '');
    setValue('sdiCode', donor.sdiCode || '');
    
    onChange(donor);
    setShowSearchResults(false);
  };


  const onSubmit = (formData: DonorFormData) => {
    onChange(formData);
    onNext();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dati del Donatore
      </Typography>

      {showSearchResults && searchResults.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            onClose={() => setShowSearchResults(false)}
          >
            <Typography variant="body2" gutterBottom>
              Trovati {searchResults.length} donatori con email simile:
            </Typography>
          </Alert>
          <Box>
            {searchResults.slice(0, 3).map((donor, index) => (
              <Card key={`donor_${index}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6">
                    {donor.firstName} {donor.lastName}
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={() => handleSelectDonor(donor)}
                  >
                    Seleziona
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome *"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                disabled={disabled}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Cognome *"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                disabled={disabled}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
              />
            )}
          />
        </Box>

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Email *"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={disabled}
              sx={{ mt: 2 }}
              onChange={(e) => handleFormChange('email', e.target.value)}
              InputProps={{
                endAdornment: isSearching ? <CircularProgress size={20} /> : null,
              }}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Telefono"
              type="tel"
              disabled={disabled}
              sx={{ mt: 2 }}
              onChange={(e) => handleFormChange('phone', e.target.value)}
            />
          )}
        />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '2fr 1fr 1fr', mt: 2 }}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Indirizzo"
                disabled={disabled}
                onChange={(e) => handleFormChange('address', e.target.value)}
              />
            )}
          />

          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={ITALIAN_CITIES}
                freeSolo
                disabled={disabled}
                onChange={(_, value) => handleFormChange('city', value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Città"
                    onChange={(e) => handleFormChange('city', e.target.value)}
                  />
                )}
              />
            )}
          />

          <Controller
            name="postalCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="CAP"
                disabled={disabled}
                onChange={(e) => handleFormChange('postalCode', e.target.value)}
              />
            )}
          />
        </Box>

        <Controller
          name="company"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Azienda"
              disabled={disabled}
              sx={{ mt: 2 }}
              onChange={(e) => handleFormChange('company', e.target.value)}
            />
          )}
        />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr 1fr', mt: 2 }}>
          <Controller
            name="fiscalCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Codice Fiscale"
                disabled={disabled}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  handleFormChange('fiscalCode', value);
                }}
                inputProps={{
                  maxLength: 16,
                  style: { textTransform: 'uppercase' }
                }}
                helperText="16 caratteri alfanumerici"
              />
            )}
          />

          <Controller
            name="vatNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Partita IVA"
                disabled={disabled}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Solo numeri
                  handleFormChange('vatNumber', value);
                }}
                inputProps={{
                  maxLength: 11,
                }}
                helperText="11 cifre"
              />
            )}
          />

          <Controller
            name="sdiCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Codice SDI"
                disabled={disabled}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  handleFormChange('sdiCode', value);
                }}
                inputProps={{
                  maxLength: 7,
                  style: { textTransform: 'uppercase' }
                }}
                helperText="6-7 caratteri alfanumerici"
              />
            )}
          />

          <Controller
            name="pec"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="PEC"
                disabled={disabled}
                onChange={(e) => handleFormChange('pec', e.target.value)}
                helperText="Indirizzo PEC (opzionale)"
                sx={{ mt: 2 }}
              />
            )}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Controller
            name="gdprConsent"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={disabled}
                    onChange={(e) => handleFormChange('gdprConsent', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Accetto il trattamento dei dati personali secondo il GDPR *
                  </Typography>
                }
              />
            )}
          />
          {errors.gdprConsent && (
            <Typography variant="caption" color="error" display="block">
              {errors.gdprConsent.message}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Controller
            name="marketingConsent"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    disabled={disabled}
                    onChange={(e) => handleFormChange('marketingConsent', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Accetto di ricevere comunicazioni promozionali (opzionale)
                  </Typography>
                }
              />
            )}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || disabled}
            size="large"
          >
            Avanti
          </Button>
        </Box>
      </form>
    </Box>
  );
};
